import React, { Component } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner
} from 'reactstrap';
import swal from 'sweetalert2';
import PKCEHelper from '../../../services/PKCEHelper';
import DuendeTokenManager from '../../../services/DuendeTokenManager';
import ApiCall from '../../../services/ApiCall';

class OAuthCallback extends Component {
  state = {
    status: 'processing',
    message: 'Processing authentication...',
    error: null
  }

  componentDidMount() {
    this.handleCallback();
  }

  /**
   * Handle OAuth callback from Duende
   */
  handleCallback = async () => {
    try {
      console.group('[DuendeSSO] OAuth Callback');
      console.time('[DuendeSSO] Callback Duration');
      console.info('[DuendeSSO] Start processing callback');
      // Parse URL parameters; support hash router (/astra/#/oauth/callback?code=...)
      // Primary: window.location.search; Fallback: query part after '#'
      let urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.get('code') && window.location.hash) {
        const hashQuery = window.location.hash.split('?')[1] || '';
        urlParams = new URLSearchParams(hashQuery);
      }

      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');

      console.log('OAuth callback received:', {
        url: window.location.href,
        search: window.location.search,
        code: code ? code.substring(0, 20) + '...' : 'missing',
        state: state ? state.substring(0, 20) + '...' : 'missing',
        error: error || 'none'
      });

      console.info('[DuendeSSO] Parsed params', {
        hasCode: !!code,
        hasState: !!state,
        hasError: !!error
      });

      // Check for errors from authorization server
      if (error) {
        throw new Error(errorDescription || `Authorization error: ${error}`);
      }

      // Validate required parameters
      if (!code) {
        throw new Error('Authorization code not received');
      }

      this.setState({ message: 'Validating authorization...' });

      // Retrieve and validate PKCE data
      const { codeVerifier, state: storedState } = PKCEHelper.retrieveAndClearPKCE();
      console.info('[DuendeSSO] Retrieved PKCE data', {
        hasCodeVerifier: !!codeVerifier,
        hasStoredState: !!storedState
      });

      if (!codeVerifier) {
        throw new Error('PKCE verification failed: code_verifier not found');
      }

      // Validate state to prevent CSRF
      console.info('[DuendeSSO] Validating state');
      if (state !== storedState) {
        throw new Error('State validation failed: possible CSRF attack');
      }

      this.setState({ message: 'Exchanging authorization code...' });

      // Exchange authorization code for tokens
      console.info('[DuendeSSO] Exchanging authorization code for tokens');

      const tokenResponse = await ApiCall.Duende.exchangeCodeForTokens(code, codeVerifier);

      if (!tokenResponse.data || !tokenResponse.data.access_token) {
        throw new Error('Failed to obtain access token');
      }

      const tokens = tokenResponse.data;
      console.info('[DuendeSSO] Token exchange success', {
        hasAccessToken: !!tokens.access_token,
        hasRefreshToken: !!tokens.refresh_token,
        hasIdToken: !!tokens.id_token,
        expiresIn: tokens.expires_in
      });

      this.setState({ message: 'Retrieving user information...' });

      // Get user info from Duende
      console.info('[DuendeSSO] Fetching user info from Duende');
      const userInfoResponse = await ApiCall.Duende.getDuendeUserInfo(tokens.access_token);
      console.log('[DuendeSSO] userInfoResponse', userInfoResponse);

      if (!userInfoResponse.data || !userInfoResponse.data.duende.rawDuendeData.userInfo) {
        throw new Error('Failed to retrieve user information');
      }

      const userInfo = userInfoResponse.data.duende.rawDuendeData.userInfo;
      console.info('[DuendeSSO] User info received', {
        subject: userInfo.sub,
        email: userInfo.email,
        name: userInfo.name
      });

      this.setState({ message: 'Synchronizing with AGC system...' });

      // Sync user with AGC backend
      console.info('[DuendeSSO] Syncing user with AGC backend');
      console.info('tokens', tokens);
      const syncResponse = await ApiCall.Duende.syncDuendeUser(userInfo, tokens.access_token);
      console.log('syncResponse', syncResponse);

      if (!syncResponse.data || syncResponse.status !== 200) {
        throw new Error('Failed to synchronize user with AGC system');
      }

      const agcUser = syncResponse.data.user;
      console.info('[DuendeSSO] Sync completed', {
        success: syncResponse.data.success,
        hasLegacyToken: !!syncResponse.data.token,
        agcUserId: agcUser && agcUser.id
      });

      this.setState({ message: 'Storing authentication data...' });

      // Store Duende tokens
      console.info('[DuendeSSO] Storing Duende tokens');
      DuendeTokenManager.storeTokens(tokens);

      // Store user info
      console.info('[DuendeSSO] Storing user info');
      DuendeTokenManager.storeUserInfo(userInfo);

      // Store AGC user data in legacy format for compatibility
      if (syncResponse.data.token) {
        console.info('[DuendeSSO] Storing legacy token for compatibility');
        localStorage.setItem('token', syncResponse.data.token);
      }

      this.setState({
        status: 'success',
        message: 'Authentication successful! Redirecting...'
      });
      console.info('[DuendeSSO] Authentication success, preparing redirect');

      // Show success message
      await swal({
        title: 'Welcome!',
        text: `Hello, ${userInfo.name || userInfo.email}`,
        type: 'success',
        timer: 2000,
        showConfirmButton: false
      });

      // Persist success so landing page/login can show notification if user returns
      try {
        sessionStorage.setItem('duende_sso_success', userInfo.name || userInfo.email || 'User');
        console.info('[DuendeSSO] Stored duende_sso_success flag');
      } catch (e) { }

      // Redirect to home
      console.info('[DuendeSSO] Redirecting to /astra/#/home');
      setTimeout(() => {
        window.location = '/astra/#/home';
      }, 1000);

    } catch (error) {
      console.error('OAuth callback error:', error);
      console.info('[DuendeSSO] Error occurred, will redirect to /astra/#/login');

      this.setState({
        status: 'error',
        message: 'Authentication failed',
        error: error.message || 'An unexpected error occurred'
      });

      // Show error message
      await swal({
        title: 'Authentication Failed',
        text: error.message || 'An unexpected error occurred during authentication',
        type: 'error'
      });

      // Persist error so login page can display notification
      try {
        sessionStorage.setItem('duende_sso_error', error.message || 'Authentication failed');
      } catch (e) { }

      // Redirect back to login after error
      setTimeout(() => {
        window.location = '/astra/#/login';
      }, 2000);
    } finally {
      try {
        console.timeEnd('[DuendeSSO] Callback Duration');
      } catch (e) { }
      try {
        console.groupEnd();
      } catch (e) { }
    }
  }

  render() {
    const { status, message, error } = this.state;

    return (
      <div className="app flex-row align-items-center" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Container>
          <Row className="justify-content-center">
            <Col md="8">
              <Card>
                <CardBody className="text-center p-5">
                  {status === 'processing' && (
                    <>
                      <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                      <h3 className="mt-4">{message}</h3>
                      <p className="text-muted">Please wait...</p>
                    </>
                  )}

                  {status === 'success' && (
                    <>
                      <i className="fa fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                      <h3 className="mt-4 text-success">{message}</h3>
                    </>
                  )}

                  {status === 'error' && (
                    <>
                      <i className="fa fa-times-circle text-danger" style={{ fontSize: '4rem' }}></i>
                      <h3 className="mt-4 text-danger">{message}</h3>
                      {error && (
                        <div className="alert alert-danger mt-3">
                          {error}
                        </div>
                      )}
                      <p className="text-muted mt-3">Redirecting to login page...</p>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default OAuthCallback;
